import { createContext, useContext, useEffect, useState, useRef } from 'react'
import { supabase } from '../config/supabase'

const AuthContext = createContext(null)

const INACTIVITY_LIMIT = 30 * 60 * 1000 // 30 minutes

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const inactivityTimer = useRef(null)

  const resetInactivityTimer = () => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current)
    }

    inactivityTimer.current = setTimeout(async () => {
      await supabase.auth.signOut()
      setUser(null)
      setSession(null)
    }, INACTIVITY_LIMIT)
  }

  useEffect(() => {
    const initSession = async () => {
      const { data } = await supabase.auth.getSession()
      setSession(data.session)
      setUser(data.session?.user ?? null)
      setLoading(false)
    }

    initSession()

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      }
    )

    const activityEvents = ['mousemove', 'keydown', 'click']
    activityEvents.forEach(event =>
      window.addEventListener(event, resetInactivityTimer)
    )

    resetInactivityTimer()

    return () => {
      authListener.subscription.unsubscribe()
      activityEvents.forEach(event =>
        window.removeEventListener(event, resetInactivityTimer)
      )
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current)
      }
    }
  }, [])

  const login = async (email, password) => {
    setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    setLoading(false)
    if (error) throw error
  }

  const logout = async () => {
    await supabase.auth.signOut()
    setUser(null)
    setSession(null)
  }

  const isAdmin = user?.user_metadata?.role === 'admin'

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAdmin,
        loading,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
